<script setup lang="ts" generic="TData extends { name: string }, TValue">
    import type {
        ColumnDef,
        SortingState,
        ColumnFiltersState,
        RowSelectionState,
    } from '@tanstack/vue-table';
    import {
        FlexRender,
        getCoreRowModel,
        useVueTable,
        getPaginationRowModel,
        getSortedRowModel,
        getFilteredRowModel,
    } from '@tanstack/vue-table';
    import { ref } from 'vue';

    import {
        Table,
        TableBody,
        TableCell,
        TableHead,
        TableHeader,
        TableRow,
    } from '@/components/ui/table';

    import DebouncedInput from '@/components/DebouncedInput.vue';
    import { valueUpdater } from '@/lib/utils';
    import Icon from '@/components/Icon.vue';
    import Loading from '@/components/Loading.vue';
    import {
        booleanFilterFn,
        valueInArrayFilterFn,
        dateRangeFilterFn,
        periodRangeFilterFn,
    } from '@/utils/filterFunctions';

    const props = withDefaults(
        defineProps<{
            columns: ColumnDef<TData, TValue>[];
            data: TData[];
            loading?: boolean;
            isError?: boolean;
            defaultSorting?: SortingState;
            defaultColumnFilters?: ColumnFiltersState;
        }>(),
        {
            defaultSorting: () => [{ id: 'count', desc: true }],
            defaultGrouping: () => [],
            defaultColumnVisibility: () => ({}),
            defaultColumnFilters: () => [],
            manualPagination: false,
            hideControls: false,
        },
    );

    const sorting = ref<SortingState>(props.defaultSorting);
    const columnFilters = ref<ColumnFiltersState>(props.defaultColumnFilters);
    const rowSelection = ref<RowSelectionState>({});
    const globalFilter = ref('');

    const emit = defineEmits<{
        'row-selection-change': [rowSelection: string[]];
    }>();

    const table = useVueTable({
        get data() {
            return props.data;
        },
        get columns() {
            return props.columns;
        },
        get rowCount() {
            return props.data.length;
        },

        manualPagination: true,
        enableRowSelection: true,
        enableMultiRowSelection: false,
        filterFns: {
            boolean: booleanFilterFn,
            valueInArray: valueInArrayFilterFn,
            dateRange: dateRangeFilterFn,
            periodRange: periodRangeFilterFn,
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getRowId: (row) => row.name,
        onSortingChange: (updaterOrValue) => valueUpdater(updaterOrValue, sorting),
        onColumnFiltersChange: (updaterOrValue) => valueUpdater(updaterOrValue, columnFilters),
        onRowSelectionChange: (updaterOrValue) => {
            valueUpdater(updaterOrValue, rowSelection);
            const selectedRows = table.getFilteredSelectedRowModel().rows;
            const selectedValues = selectedRows.map((row: any) => row.original.name);
            emit('row-selection-change', selectedValues);
        },
        onGlobalFilterChange: (updaterOrValue) => valueUpdater(updaterOrValue, globalFilter),
        globalFilterFn: (row, columnId, filterValue) => {
            const search = String(filterValue).toLowerCase();
            let currentRow = row;
            while (currentRow.parentId) {
                const parentRow = table.getRow(currentRow.parentId);
                if (String(parentRow.getValue(columnId)).toLowerCase().includes(search)) {
                    return true;
                }
                currentRow = parentRow;
            }
            return String(row.getValue(columnId)).toLowerCase().includes(search);
        },

        state: {
            get sorting() {
                return sorting.value;
            },
            get columnFilters() {
                return columnFilters.value;
            },
            get rowSelection() {
                return rowSelection.value;
            },
            get globalFilter() {
                return globalFilter.value;
            },
        },
    });
</script>

<template>
    <div class="flex h-full flex-col gap-2">
        <DebouncedInput
            :placeholder="$t('datatables.search_placeholder')"
            :modelValue="globalFilter ?? ''"
            @update:modelValue="(value: string) => (globalFilter = String(value))"
            :debounce="200"
            class="min-h-7.5"
        />
        <div class="flex min-h-0 flex-1 flex-col rounded border">
            <Loading v-if="!table.getRowModel().rows?.length && loading" />
            <Table v-else>
                <TableHeader>
                    <TableRow
                        v-for="headerGroup in table.getHeaderGroups()"
                        :key="headerGroup.id"
                    >
                        <TableHead
                            v-for="header in headerGroup.headers"
                            :key="header.id"
                            :style="
                                header.column.getIsPinned() === 'left' && {
                                    left: `${header.column.getStart('left')}px`,
                                }
                            "
                        >
                            <FlexRender
                                v-if="!header.isPlaceholder"
                                :render="header.column.columnDef.header"
                                :props="header.getContext()"
                            />
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <template v-if="table.getRowModel().rows?.length">
                        <template
                            v-for="row in table.getRowModel().rows"
                            :key="row.id"
                        >
                            <TableRow :data-state="row.getIsSelected() ? 'selected' : undefined">
                                <TableCell
                                    v-for="cell in row.getVisibleCells()"
                                    :key="cell.id"
                                    :style="
                                        cell.column.getIsPinned() === 'left' && {
                                            left: `${cell.column.getStart('left')}px`,
                                        }
                                    "
                                >
                                    <template v-if="cell.getIsAggregated()">
                                        <FlexRender
                                            :render="cell.column.columnDef.cell"
                                            :props="cell.getContext()"
                                        />
                                    </template>
                                    <template v-else>
                                        <FlexRender
                                            :render="cell.column.columnDef.cell"
                                            :props="cell.getContext()"
                                        />
                                    </template>
                                </TableCell>
                            </TableRow>
                        </template>
                    </template>
                    <template v-else>
                        <TableRow>
                            <TableCell
                                :colspan="columns.length"
                                class="h-24 text-center"
                            >
                                <div class="flex flex-col items-center gap-2">
                                    <Icon
                                        v-if="isError"
                                        name="alertCircle"
                                        class="text-destructive size-6"
                                    />
                                    <span>{{ $t('datatables.infoEmpty') }}</span>
                                </div>
                            </TableCell>
                        </TableRow>
                    </template>
                </TableBody>
            </Table>
        </div>
    </div>
</template>
